import type { QueryClient } from "@tanstack/react-query";

import type { LoyaltyPointState } from "@/types/loyalty-points";

import {
  type LoyaltyPointGrantListParams,
  getAvailableLoyaltyPoints,
  getLoyaltyPointsLog,
  getPatientLoyaltyPoints,
} from "./loyalty-points";

const LOYALTY_POINT_STATE_STALE_TIME = 30_000;

export const managedLoyaltyPointGrantParams = {
  perPage: 100,
  includeUsed: true,
} satisfies LoyaltyPointGrantListParams;

export const loyaltyPointQueryKeys = {
  all: ["user", "patient", "loyalty-points"] as const,
  patient: (patientId: string) =>
    [...loyaltyPointQueryKeys.all, patientId] as const,
  grants: (patientId: string, params?: LoyaltyPointGrantListParams) =>
    [...loyaltyPointQueryKeys.patient(patientId), "grants", params] as const,
  available: (patientId: string) =>
    [...loyaltyPointQueryKeys.patient(patientId), "available"] as const,
  log: (patientId: string, grantId?: string) =>
    [...loyaltyPointQueryKeys.patient(patientId), "log", grantId] as const,
};

export const loyaltyPointQueryOptions = {
  grants: (patientId: string, params?: LoyaltyPointGrantListParams) => ({
    queryKey: loyaltyPointQueryKeys.grants(patientId, params),
    queryFn: () => getPatientLoyaltyPoints(patientId, params),
    staleTime: LOYALTY_POINT_STATE_STALE_TIME,
  }),
  available: (patientId: string) => ({
    queryKey: loyaltyPointQueryKeys.available(patientId),
    queryFn: () => getAvailableLoyaltyPoints(patientId),
    staleTime: LOYALTY_POINT_STATE_STALE_TIME,
  }),
  log: (patientId: string, grantId?: string) => ({
    queryKey: loyaltyPointQueryKeys.log(patientId, grantId),
    queryFn: () => getLoyaltyPointsLog(patientId, grantId),
    staleTime: LOYALTY_POINT_STATE_STALE_TIME,
  }),
};

export function seedLoyaltyPointMutationCaches(
  queryClient: QueryClient,
  patientId: string,
  state: LoyaltyPointState,
): void {
  queryClient.setQueryData(
    loyaltyPointQueryKeys.grants(patientId, managedLoyaltyPointGrantParams),
    state.grants,
  );
  queryClient.setQueryData(
    loyaltyPointQueryKeys.available(patientId),
    state.available,
  );
  queryClient.setQueryData(loyaltyPointQueryKeys.log(patientId), state.log);
}
